
import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { issuePoints } from '@/services/databaseService';
import { toast } from 'sonner';

interface PointsIssueModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  profileName?: string;
  onPointsIssued?: () => void;
}

const PointsIssueModal = ({ 
  open, 
  onOpenChange, 
  userId,
  profileName,
  onPointsIssued
}: PointsIssueModalProps) => {
  const [pointsAmount, setPointsAmount] = useState<number>(0);
  const [transactionId, setTransactionId] = useState<number>(Math.floor(Math.random() * 1000000));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (pointsAmount <= 0) {
      toast.error("Please enter a valid points amount");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await issuePoints(userId, pointsAmount, transactionId);
      
      if (result.success) {
        toast.success(`${pointsAmount} points issued successfully`);
        setPointsAmount(0);
        setTransactionId(Math.floor(Math.random() * 1000000));
        onOpenChange(false);
        if (onPointsIssued) onPointsIssued();
      } else {
        toast.error("Failed to issue points");
      }
    } catch (error) {
      console.error("Error issuing points:", error);
      toast.error("An error occurred while issuing points");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-xl mb-4">
            Issue Points {profileName ? `to ${profileName}` : ''}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pointsAmount">Points Amount</Label>
            <Input
              id="pointsAmount"
              type="number"
              value={pointsAmount}
              onChange={(e) => setPointsAmount(Number(e.target.value))}
              min={1}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="transactionId">Transaction ID</Label>
            <Input
              id="transactionId"
              type="number"
              value={transactionId}
              onChange={(e) => setTransactionId(Number(e.target.value))}
              required
              readOnly
            />
            <small className="text-gray-500">Auto-generated transaction ID</small>
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Processing...
                </>
              ) : (
                'Issue Points'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PointsIssueModal;
